import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { TaskSummary } from "../domain/entities";

export type TaskCacheSnapshot = Array<[QueryKey, unknown]>;

const taskQuery = (key: QueryKey) => ["view", "task", "project", "area", "tag"].includes(String(key[0] ?? ""));

export async function snapshotTaskCache(queryClient: QueryClient): Promise<TaskCacheSnapshot> {
  await queryClient.cancelQueries({ predicate: (query) => taskQuery(query.queryKey) });
  return queryClient.getQueriesData({ predicate: (query) => taskQuery(query.queryKey) });
}

export function restoreTaskCache(queryClient: QueryClient, snapshot: TaskCacheSnapshot | undefined): void {
  for (const [key, value] of snapshot ?? []) queryClient.setQueryData(key, value);
}

export function updateTaskCache(queryClient: QueryClient, taskId: string, update: (task: TaskSummary) => TaskSummary | null, options: { includeDetail?: boolean } = {}): void {
  queryClient.setQueriesData({ predicate: (query) => taskQuery(query.queryKey) && (options.includeDetail !== false || query.queryKey[0] !== "task") }, (value) => transform(value, taskId, update));
}

function transform(value: unknown, taskId: string, update: (task: TaskSummary) => TaskSummary | null): unknown {
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  if (record.id === taskId && "scheduleKind" in record && "status" in record) return update(record as unknown as TaskSummary) ?? undefined;
  if (Array.isArray(record.items)) {
    const before = record.items;
    const items = before.flatMap((item) => {
      if (!item || typeof item !== "object" || (item as { id?: string }).id !== taskId || !("scheduleKind" in item)) return [item];
      const next = update(item as TaskSummary);
      return next ? [next] : [];
    });
    if (items.length !== before.length || items.some((item, index) => item !== before[index])) {
      return { ...record, items, total: typeof record.total === "number" ? Math.max(0, record.total - (before.length - items.length)) : record.total };
    }
  }
  if (record.tasks && typeof record.tasks === "object") {
    const tasks = transform(record.tasks, taskId, update);
    if (tasks !== record.tasks) return { ...record, tasks };
  }
  return value;
}
